import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TransactionEditor } from '../components/TransactionEditor.js';
import { Badge, ConceptName, Empty } from '../components/ui.js';
import { eur, periodLabel } from '../format.js';
import { useApp } from '../state.js';

export function TransactionsPage() {
  const { movements, catalog, balance } = useApp();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<string>(params.get('concepto') ?? 'all');
  const [editing, setEditing] = useState<string | null | 'new'>(null);
  useEffect(() => {
    const open = params.get('abrir');
    if (open) {
      setEditing(open);
      params.delete('abrir');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);
  const conceptById = useMemo(() => new Map((catalog?.concepts ?? []).map((c) => [c.id, c])), [catalog]);
  const allocsByTx = useMemo(() => {
    const m = new Map<string, typeof movements extends null ? never : NonNullable<typeof movements>['allocations']>();
    for (const a of movements?.allocations ?? []) {
      const l = m.get(a.transactionId) ?? [];
      l.push(a);
      m.set(a.transactionId, l);
    }
    return m;
  }, [movements]);
  const receiptTx = useMemo(() => new Set((movements?.receipts ?? []).map((r) => r.transactionId).filter(Boolean)), [movements]);
  const rows = useMemo(() => {
    const list = movements?.transactions ?? [];
    return list.filter((t) => {
      const allocs = allocsByTx.get(t.id) ?? [];
      const allocated = allocs.reduce((a, x) => a + x.amountCents, 0);
      const pending = !t.isInternalTransfer && !receiptTx.has(t.id) && allocated < t.amountCents;
      if (filter === 'pending' && !pending) return false;
      if (filter === 'internal' && !t.isInternalTransfer) return false;
      if (filter !== 'all' && filter !== 'pending' && filter !== 'internal' && !allocs.some((a) => a.conceptId === filter)) return false;
      if (query && !t.description.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [movements, allocsByTx, receiptTx, filter, query]);
  if (!movements || !catalog || !balance) return <div className="skeleton" />;
  return (
    <div className="page">
      <div className="panel">
        <div className="panelhead">
          <div>
            <h2>Movimientos del hogar</h2>
            <div className="muted">{movements.transactions.length} movimientos · carga manual, efectivo y lo que llegue por aviso</div>
          </div>
          <button type="button" className="button primary" onClick={() => setEditing('new')}>
            + Carga manual
          </button>
        </div>
        <div className="filters">
          <input placeholder="Buscar descripción…" aria-label="Buscar" value={query} onChange={(e) => setQuery(e.target.value)} />
          <select aria-label="Filtro" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">Todos</option>
            <option value="pending">Pendientes de clasificar</option>
            <option value="internal">Transferencias internas</option>
            {catalog.concepts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Descripción</th>
                <th>Imputación</th>
                <th className="number">Importe</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <Empty>No hay movimientos con esos filtros.</Empty>
                  </td>
                </tr>
              ) : null}
              {rows.map((t) => {
                const allocs = allocsByTx.get(t.id) ?? [];
                const allocated = allocs.reduce((a, x) => a + x.amountCents, 0);
                const isReceipt = receiptTx.has(t.id);
                const pending = !t.isInternalTransfer && !isReceipt && allocated < t.amountCents;
                return (
                  <tr key={t.id} className="clickrow" onClick={() => setEditing(t.id)}>
                    <td className="muted">
                      {t.date}
                      {t.time ? <div className="tiny">{t.time}</div> : null}
                    </td>
                    <td>
                      {t.description}
                      <div className="muted">
                        {t.source}
                        {allocs.length > 1 ? ` · ${allocs.length} partes` : ''}
                        {allocs.some((a) => a.recoverableCents > 0) ? ' · con recuperable' : ''}
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {t.isInternalTransfer ? <Badge kind="neutral">Transferencia propia</Badge> : null}
                      {isReceipt ? <Badge>Devolución cobrada</Badge> : null}
                      {allocs.map((a) => {
                        const c = conceptById.get(a.conceptId);
                        return (
                          <div key={a.id}>
                            {c ? <ConceptName id={c.id} name={c.name} /> : '—'} <span className="muted">· {periodLabel(a.period, 'short')}{allocs.length > 1 ? ` · ${eur(a.amountCents)}` : ''}</span>
                          </div>
                        );
                      })}
                      {pending ? <Badge kind="warn">{allocated ? `Sin repartir ${eur(t.amountCents - allocated)}` : 'Pendiente'}</Badge> : null}
                    </td>
                    <td className={`number ${t.direction === 'in' ? 'positive' : ''}`}>
                      {t.direction === 'in' ? '+' : '−'}
                      {eur(t.amountCents)}
                    </td>
                    <td>
                      <button type="button" className="button small" onClick={(e) => { e.stopPropagation(); setEditing(t.id); }}>
                        Abrir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {editing ? <TransactionEditor transactionId={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}
